import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import Head from 'expo-router/head';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ChevronLeft,
  Linkedin,
  ArrowRight,
  Rocket,
  TrendingUp,
  Play,
  Shield,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { lightColors } from '@/lib/colors';
import { useAlert } from '@/components/AlertDialog';
import { AuthLayout } from '@/components/AuthLayout';
import { usePhoneShell } from '@/lib/useShell';
import { isWeb } from '@/lib/platform';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { sanitizeReturnTo, getTrustedOrigin } from '@/lib/auth/return-to';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import {
  getMockScoreCriteria,
  getMockDataRoomDocuments,
  getMockRecommendations,
  getMockInvestorMatches,
  getMockDealflow,
  getMockInvestorProfiles,
  getMockInvestorUsers,
  getMockStartups,
} from '@/lib/state/mock-data-provider';
import { capitalizeWords } from '@/lib/format';
import { useTrackScreen } from '@/lib/analytics';
import { APP_MODE, activateDemoBypass, mode } from '@/lib/config/app-mode';
import { useMutation } from '@tanstack/react-query';
import { handleError, reportError } from '@/lib/errors';
import { useScoreStore } from '@/lib/state/score-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useDataroomStore } from '@/lib/state/dataroom-store';

import { LOGO_MARK } from '@/lib/logo';

type AuthMode = 'login' | 'signup';

// --- Password Validation (signup only) ---

function validatePassword(password: string): { valid: boolean; message: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long.' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must include at least one uppercase letter.' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must include at least one lowercase letter.' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must include at least one number.' };
  }
  return { valid: true, message: '' };
}

// --- Sanitize Supabase error messages for users ---

function sanitizeAuthError(rawMessage: string): string {
  const lower = rawMessage.toLowerCase();
  if (lower.includes('invite-only') || lower.includes('waitlist') || lower.includes('p0403')) {
    return rawMessage.includes('haven\'t been invited')
      ? rawMessage
      : 'Registration is invite-only during the beta. Please join the waitlist at inverio.ai first.';
  }
  if (lower.includes('invalid login credentials') || lower.includes('invalid password')) {
    return 'Incorrect email or password.';
  }
  if (lower.includes('user already registered') || lower.includes('already exists')) {
    return 'An account with this email already exists.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please check your email to confirm your account.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  // Default: return a generic safe message instead of raw Supabase error
  return 'Authentication failed. Please try again.';
}

export default function LoginScreen() {
  useTrackScreen('Login');
  const router = useRouter();
  // Phone-width web starts the form at the top like native; centring it there
  // left a large gap under "Create Account" (119-07 UAT).
  const phoneWidth = usePhoneShell();
  const colors = lightColors;
  const params = useLocalSearchParams<{ role?: string; return_to?: string }>();
  const role = params.role as 'startup' | 'investor' | undefined;

  // After successful auth, send the user back to where they came from
  // (e.g. the OAuth /oauth/authorize page) instead of always to '/'.
  //
  // Validation lives in `sanitizeReturnTo`, which resolves the value with the
  // URL parser and compares ORIGINS. The prefix checks that used to be inlined
  // here could not see canonicalisation: `/\evil.example` starts with a single
  // '/', is not '//', and has no colon — yet a browser resolves it to
  // https://evil.example/. See src/lib/auth/return-to.ts.
  const postAuthDestination = React.useMemo(
    () => sanitizeReturnTo(params.return_to, getTrustedOrigin()),
    [params.return_to]
  );

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'startup' | 'investor' | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showDemoRolePicker, setShowDemoRolePicker] = useState(false);
  const isManualSignIn = React.useRef(false);

  // The effective role is either from URL params or user selection
  const effectiveRole = role || selectedRole;

  // Alert hook for web compatibility
  const { alert, error: showError, confirm } = useAlert();

  // Supabase auth (only used if configured)
  const { signIn, signUp, signInWithLinkedIn, isConfigured: supabaseConfigured, user } = useAuth();

  // Profile store actions (direct — no facade)
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const setPendingAuthRole = useProfileStore((s) => s.setPendingAuthRole);
  const setUser = useProfileStore((s) => s.setUser);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const initializeOnboarding = useProfileStore((s) => s.initializeOnboarding);
  const completeOnboarding = useProfileStore((s) => s.completeOnboarding);

  // Subscription store actions
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);
  const mockSubscribe = useSubscriptionStore((s) => s.mockSubscribe);

  // Inverio store for mock data loading (dashboard data)
  const setScoreCriteria = useScoreStore((s) => s.setScoreCriteria);
  const setDataRoomDocuments = useDataroomStore((s) => s.setDataRoomDocuments);
  const setAIRecommendations = useScoreStore((s) => s.setAIRecommendations);
  const setInvestorMatches = useScoreStore((s) => s.setInvestorMatches);
  const setDealflow = useDealflowStore((s) => s.setDealflow);

  // Handle LinkedIn OAuth callback — only fires for OAuth redirects, not programmatic sign-ins.
  // handleEmailAuth sets isManualSignIn.current = true to prevent this from racing.
  useEffect(() => {
    if (user && isSupabaseConfigured() && !isManualSignIn.current) {
      const meta = user.user_metadata;
      setUser({
        id: user.id,
        name: meta?.full_name || meta?.name || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        avatarUrl: meta?.avatar_url || meta?.picture || undefined,
      });
      router.replace(postAuthDestination);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailAuthMutation = useMutation({
    mutationFn: async () => {
      isManualSignIn.current = true;

      if (supabaseConfigured) {
        if (mode === 'signup' && !effectiveRole) {
          alert('Missing Information', 'Please select whether you are a startup or an investor.');
          return;
        }

        try {
          if (mode === 'signup') {
            setPendingAuthRole(effectiveRole!);
            // Pass the "Full Name" through so handle_new_user stores it on the
            // profile — otherwise onboarding has nothing to prefill (Adi 2026-07-22).
            const trimmedName = name.trim();
            const firstSpace = trimmedName.indexOf(' ');
            const firstName = (firstSpace === -1 ? trimmedName : trimmedName.slice(0, firstSpace)) || undefined;
            const lastName = firstSpace === -1 ? undefined : (trimmedName.slice(firstSpace + 1).trim() || undefined);
            const { error, needsEmailConfirmation } = await signUp(email, password, effectiveRole!, firstName, lastName);
            if (error) {
              showError('Sign Up Failed', sanitizeAuthError(error.message));
              return;
            }

            if (needsEmailConfirmation) {
              alert(
                'Check Your Email',
                'We sent a confirmation link to your email address. Please click the link to activate your account, then return here to sign in.'
              );
              setMode('login');
              return;
            }

            setUser({
              name: capitalizeWords(name.trim()),
              email: email.trim(),
            });

            setUserRole(effectiveRole!);
            setSubscriptionUserRole(effectiveRole!);
            initializeOnboarding(effectiveRole!);
            router.replace(postAuthDestination);
          } else {
            const { error, mfaRequired, mfaChallenge } = await signIn(email, password);
            if (error) {
              showError('Sign In Failed', sanitizeAuthError(error.message));
              return;
            }
            if (mfaRequired && mfaChallenge) {
              router.push({
                pathname: '/mfa-verify',
                params: {
                  factorId: mfaChallenge.factorId,
                  challengeId: mfaChallenge.challengeId,
                  role: effectiveRole || '',
                },
              });
              return;
            }
            router.replace(postAuthDestination);
          }
        } catch (err) {
          const { message, isCancelled } = handleError(err, { context: 'handleEmailAuth' });
          if (!isCancelled) showError('Error', message);
        }
      } else if (effectiveRole) {
        await new Promise(resolve => setTimeout(resolve, 1500));

        if (mode === 'signup' && name.trim()) {
          setUser({
            name: capitalizeWords(name.trim()),
            email: email.trim(),
          });
        }

        setUserRole(effectiveRole);
        setSubscriptionUserRole(effectiveRole);
        initializeOnboarding(effectiveRole);
        router.replace(postAuthDestination);
      }
    },
  });

  const handleEmailAuth = () => {
    if (!email.trim() || !password.trim()) {
      alert('Missing Information', 'Please enter your email and password.');
      return;
    }

    if (mode === 'signup' && !name.trim()) {
      alert('Missing Information', 'Please enter your name.');
      return;
    }

    if (mode === 'signup' && !effectiveRole) {
      alert('Missing Information', 'Please select whether you are a startup or an investor.');
      return;
    }

    if (mode === 'signup') {
      const passwordCheck = validatePassword(password);
      if (!passwordCheck.valid) {
        setPasswordError(passwordCheck.message);
        return;
      }
      setPasswordError(null);
    }

    emailAuthMutation.mutate();
  };

  const linkedInAuthMutation = useMutation({
    mutationFn: async () => {
      if (supabaseConfigured) {
        try {
          const { error } = await signInWithLinkedIn();
          if (error) {
            showError('LinkedIn Sign In Failed', sanitizeAuthError(error.message));
          }
        } catch (err) {
          const { message, isCancelled } = handleError(err, { context: 'handleLinkedInAuth' });
          if (!isCancelled) showError('LinkedIn Error', message);
        }
        return;
      }

      const roleToUse = effectiveRole;
      const confirmed = await confirm(
        'LinkedIn Authentication',
        'This would open LinkedIn OAuth flow. For demo purposes, we\'ll simulate a successful login.'
      );

      if (confirmed && roleToUse) {
        try {
          setUserRole(roleToUse);
          setSubscriptionUserRole(roleToUse);
          initializeOnboarding(roleToUse);
          router.replace(postAuthDestination);
        } catch (err) {
          const { message, isCancelled } = handleError(err, { context: 'handleLinkedInAuth.mock' });
          if (!isCancelled) showError('Login Error', message);
        }
      }
    },
  });

  const isLoading = emailAuthMutation.isPending || linkedInAuthMutation.isPending;

  const handleLinkedInAuth = () => {
    if (mode === 'signup' && !effectiveRole) {
      alert('Select Your Role', 'Please select whether you are a startup or an investor before continuing.');
      return;
    }

    if (effectiveRole) {
      setPendingAuthRole(effectiveRole);
      if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('pendingAuthRole', effectiveRole);
      }
    }

    linkedInAuthMutation.mutate();
  };

  // Demo mode shortcut (available in non-production on all platforms)
  const showDemoButton = APP_MODE !== 'production';

  const handleTryDemo = (overrideRole?: 'startup' | 'investor' | 'admin') => {
    // Demo bypass is purely local — no Supabase auth, no network calls, no races.
    // activateDemoBypass() makes mode.isMock true, so auth guards pass and
    // mock data functions return data. Everything is synchronous and instant.
    activateDemoBypass();

    const demoRole = overrideRole || effectiveRole || 'startup';

    // 1. Set identity
    setUserRole(demoRole);
    setSubscriptionUserRole(demoRole === 'admin' ? 'startup' : demoRole);
    setUser({
      name: demoRole === 'admin' ? 'Admin User' : demoRole === 'startup' ? 'Maude Girod' : 'Alex Hartmann',
      email: `demo-${demoRole}@inverio.ai`,
    });

    // 2. Mark onboarding complete (admin uses startup onboarding state)
    initializeOnboarding(demoRole === 'investor' ? 'investor' : 'startup');
    completeOnboarding();

    // 3. Load mock data for the role
    if (demoRole === 'startup') {
      const startups = getMockStartups();
      if (startups[0]) setStartupProfile(startups[0]);
      setScoreCriteria(getMockScoreCriteria());
      setDataRoomDocuments(getMockDataRoomDocuments());
      setAIRecommendations(getMockRecommendations());
      setInvestorMatches(getMockInvestorMatches());
    } else if (demoRole === 'investor') {
      const investors = getMockInvestorProfiles();
      const investorUsers = getMockInvestorUsers();
      if (investors[0]) {
        setInvestorProfile(investors[0]);
        const investorUser = investorUsers[investors[0].id];
        if (investorUser) setUser(investorUser);
      }
      setDealflow(getMockDealflow());
    }
    // Admin needs no extra data

    // 4. Navigate to landing page — it reads isMock + userRole and routes to the
    // correct dashboard. The useIsFocused guard on the landing page ensures it only
    // navigates when it becomes the active screen (no background races).
    router.replace(postAuthDestination);
  };

  // Role selection only needed during signup — returning users have role in DB profile
  const needsRoleSelection = mode === 'signup' && !role && !selectedRole;

  const roleLabels = {
    startup: 'Startup',
    investor: 'Investor',
  };

  return (
    <AuthLayout>
      {Platform.OS === 'web' && (
        <Head>
          <title>Sign In — Inverio</title>
          <meta name="description" content="Sign in to your Inverio account. Access your investment readiness score, data room, and investor matches." />
          <link rel="canonical" href="https://inverio.ai/login" />
          <meta property="og:title" content="Sign In — Inverio" />
          <meta property="og:description" content="Sign in to your Inverio account. Access your investment readiness score, data room, and investor matches." />
          <meta property="og:url" content="https://inverio.ai/login" />
        </Head>
      )}
      <View className="flex-1" style={{ backgroundColor: isWeb ? colors.surface.primary : colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
          >
            <ScrollView
              className="flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1, justifyContent: isWeb && !phoneWidth ? 'center' : 'flex-start' }}
              keyboardShouldPersistTaps="handled"
            >
              {/* Header - hide on web */}
              {!isWeb && (
                <Animated.View
                  entering={FadeInDown.delay(100).duration(500)}
                  className="px-5 pt-4"
                >
                  <AnimatedPressable
                    onPress={() => router.back()}
                    accessibilityRole="button"
                    accessibilityLabel="Go back"
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <ChevronLeft size={24} color={colors.text.primary} />
                  </AnimatedPressable>
                </Animated.View>
              )}

              {/* Logo & Title */}
              <Animated.View
                entering={FadeInDown.delay(200).duration(500)}
                className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-8 pb-8'}`}
              >
                {/* Hide logo on web desktop - shown in left panel */}
                {!isWeb && (
                  <Image
                    source={LOGO_MARK}
                    className="w-20 h-20 mb-6"
                    resizeMode="contain"
                    accessibilityRole="image"
                    accessibilityLabel="Inverio logo"
                  />
                )}
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-2">
                  {mode === 'login' ? 'Welcome Back' : 'Create Account'}
                </Text>
                {effectiveRole && (
                  <View
                    className="px-4 py-1.5 rounded-full"
                    style={{ backgroundColor: `${colors.primary[600]}15` }}
                  >
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                      {roleLabels[effectiveRole]} Account
                    </Text>
                  </View>
                )}
              </Animated.View>

              {/* Role Selection (signup without role) */}
              {needsRoleSelection && (
                <Animated.View
                  entering={FadeInUp.delay(250).duration(500)}
                  className="px-5 mb-6"
                >
                  <Text style={{ color: colors.text.secondary }} className="text-sm mb-3 font-medium text-center">
                    I am...
                  </Text>
                  <View style={{ flexDirection: 'row', gap: 12 }}>
                    <View style={{ flex: 1 }}>
                      <AnimatedPressable
                        onPress={() => setSelectedRole('startup')}
                        accessibilityRole="button"
                        accessibilityLabel="Select startup role"
                        accessibilityState={{ selected: selectedRole === 'startup' }}
                        className="rounded-xl p-4 items-center"
                        style={{
                          backgroundColor: selectedRole === 'startup' ? colors.primary[50] : colors.surface.primary,
                          borderWidth: 2,
                          borderColor: selectedRole === 'startup' ? colors.primary[600] : colors.border.light,
                        }}
                      >
                        <Rocket size={28} color={selectedRole === 'startup' ? colors.primary[600] : colors.text.tertiary} />
                        <Text
                          style={{ color: selectedRole === 'startup' ? colors.primary[600] : colors.text.secondary }}
                          className="font-semibold mt-2"
                        >
                          A Startup
                        </Text>
                      </AnimatedPressable>
                    </View>
                    <View style={{ flex: 1 }}>
                      <AnimatedPressable
                        onPress={() => setSelectedRole('investor')}
                        accessibilityRole="button"
                        accessibilityLabel="Select investor role"
                        accessibilityState={{ selected: selectedRole === 'investor' }}
                        className="rounded-xl p-4 items-center"
                        style={{
                          backgroundColor: selectedRole === 'investor' ? colors.primary[50] : colors.surface.primary,
                          borderWidth: 2,
                          borderColor: selectedRole === 'investor' ? colors.primary[600] : colors.border.light,
                        }}
                      >
                        <TrendingUp size={28} color={selectedRole === 'investor' ? colors.primary[600] : colors.text.tertiary} />
                        <Text
                          style={{ color: selectedRole === 'investor' ? colors.primary[600] : colors.text.secondary }}
                          className="font-semibold mt-2"
                        >
                          An Investor
                        </Text>
                      </AnimatedPressable>
                    </View>
                  </View>
                </Animated.View>
              )}

              {/* LinkedIn Button */}
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-5 mb-6"
              >
                <AnimatedPressable
                  onPress={handleLinkedInAuth}
                  disabled={isLoading}
                  accessibilityRole="button"
                  accessibilityLabel="Continue with LinkedIn"
                  className="rounded-xl py-4 flex-row items-center justify-center gap-3"
                  style={{ backgroundColor: '#0A66C2' }}
                >
                  <Linkedin size={22} color="white" />
                  <Text className="text-white font-semibold text-base">
                    Continue with LinkedIn
                  </Text>
                </AnimatedPressable>
              </Animated.View>

              {/* Divider */}
              <Animated.View
                entering={FadeInUp.delay(350).duration(500)}
                className="flex-row items-center px-5 mb-6"
              >
                <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
                <Text style={{ color: colors.text.tertiary }} className="px-4 text-sm">
                  or
                </Text>
                <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
              </Animated.View>

              {/* Form */}
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="px-5 gap-4"
              >
                {/* Name (signup only) */}
                {mode === 'signup' && (
                  <View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                      Full Name
                    </Text>
                    <View
                      className="flex-row items-center rounded-xl px-4"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <TextInput
                        style={{ color: colors.text.primary }}
                        className="flex-1 py-4 text-base"
                        placeholder="Enter your name"
                        placeholderTextColor={colors.text.tertiary}
                        value={name}
                        onChangeText={setName}
                        autoCapitalize="words"
                        accessibilityLabel="Full name"
                      />
                    </View>
                  </View>
                )}

                {/* Email */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                    Email Address
                  </Text>
                  <View
                    className="flex-row items-center rounded-xl px-4"
                    style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                  >
                    <Mail size={20} color={colors.text.tertiary} />
                    <TextInput
                      style={{ color: colors.text.primary }}
                      className="flex-1 py-4 pl-3 text-base"
                      placeholder="Enter your email"
                      placeholderTextColor={colors.text.tertiary}
                      value={email}
                      onChangeText={setEmail}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoComplete="email"
                      accessibilityLabel="Email address"
                    />
                  </View>
                </View>

                {/* Password */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                    Password
                  </Text>
                  <View
                    className="flex-row items-center rounded-xl px-4"
                    style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                  >
                    <Lock size={20} color={colors.text.tertiary} />
                    <TextInput
                      style={{ color: colors.text.primary }}
                      className="flex-1 py-4 pl-3 text-base"
                      placeholder="Enter your password"
                      placeholderTextColor={colors.text.tertiary}
                      value={password}
                      onChangeText={(text) => {
                        setPassword(text);
                        if (passwordError) setPasswordError(null);
                      }}
                      secureTextEntry={!showPassword}
                      autoCapitalize="none"
                      accessibilityLabel="Password"
                    />
                    <Pressable
                      onPress={() => setShowPassword(!showPassword)}
                      accessibilityRole="button"
                      accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
                      className="p-2"
                    >
                      {showPassword ? (
                        <EyeOff size={20} color={colors.text.tertiary} />
                      ) : (
                        <Eye size={20} color={colors.text.tertiary} />
                      )}
                    </Pressable>
                  </View>
                  {/* Password validation error (signup only) */}
                  {mode === 'signup' && passwordError && (
                    <Text style={{ color: '#DC2626' }} className="text-xs mt-1.5 ml-1">
                      {passwordError}
                    </Text>
                  )}
                </View>

                {/* Forgot Password (login only) */}
                {mode === 'login' && (
                  <Pressable
                    onPress={() => router.push('/forgot-password')}
                    accessibilityRole="link"
                    accessibilityLabel="Forgot password"
                    className="self-end"
                  >
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                      Forgot Password?
                    </Text>
                  </Pressable>
                )}
              </Animated.View>

              {/* Submit Button */}
              <Animated.View
                entering={FadeInUp.delay(500).duration(500)}
                className="px-5 mt-8"
              >
                <AnimatedPressable
                  onPress={handleEmailAuth}
                  disabled={isLoading}
                  accessibilityRole="button"
                  accessibilityLabel={mode === 'login' ? 'Sign in' : 'Create account'}
                  accessibilityState={{ disabled: isLoading, busy: isLoading }}
                  className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                  style={{
                    backgroundColor: isLoading ? colors.border.medium : colors.primary[600],
                  }}
                >
                  <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                    {isLoading
                      ? 'Please wait...'
                      : mode === 'login'
                      ? 'Sign In'
                      : 'Create Account'}
                  </Text>
                  {!isLoading && <ArrowRight size={20} color={colors.text.inverse} />}
                </AnimatedPressable>
              </Animated.View>

              {/* Switch Mode */}
              <Animated.View
                entering={FadeInUp.delay(600).duration(500)}
                className="flex-row items-center justify-center px-5 mt-6 pb-8"
              >
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                </Text>
                <Pressable
                  onPress={() => setMode(mode === 'login' ? 'signup' : 'login')}
                  accessibilityRole="button"
                  accessibilityLabel={mode === 'login' ? 'Switch to sign up' : 'Switch to sign in'}
                >
                  <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                    {mode === 'login' ? 'Sign Up' : 'Sign In'}
                  </Text>
                </Pressable>
              </Animated.View>

              {/* Demo Mode Button (non-production) */}
              {showDemoButton && (
                <Animated.View
                  entering={FadeInUp.delay(650).duration(500)}
                  className="px-5 mt-2"
                >
                  {/* Divider */}
                  <View className="flex-row items-center mb-4">
                    <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
                    <Text style={{ color: colors.text.tertiary }} className="px-4 text-xs">
                      or skip authentication
                    </Text>
                    <View className="flex-1 h-px" style={{ backgroundColor: colors.border.light }} />
                  </View>

                  {!showDemoRolePicker ? (
                    <AnimatedPressable
                      onPress={() => setShowDemoRolePicker(true)}
                      disabled={isLoading}
                      accessibilityRole="button"
                      accessibilityLabel="Try demo mode"
                      className="rounded-xl py-3.5 flex-row items-center justify-center gap-2"
                      style={{
                        backgroundColor: colors.surface.tertiary,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                      }}
                    >
                      <Play size={18} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.secondary }} className="font-medium text-sm">
                        Try Demo Mode
                      </Text>
                    </AnimatedPressable>
                  ) : (
                    <View>
                      <Text style={{ color: colors.text.secondary }} className="text-sm font-medium text-center mb-3">
                        Experience demo as...
                      </Text>
                      <View style={{ flexDirection: 'row', gap: 12 }}>
                        <View style={{ flex: 1 }}>
                          <AnimatedPressable
                            onPress={() => handleTryDemo('startup')}
                            disabled={isLoading}
                            accessibilityRole="button"
                            accessibilityLabel="Demo as startup"
                            className="rounded-xl py-4 items-center gap-2"
                            style={{
                              backgroundColor: colors.primary[50],
                              borderWidth: 1.5,
                              borderColor: colors.primary[300],
                            }}
                          >
                            <Rocket size={24} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[700] }} className="font-semibold text-sm">
                              Startup
                            </Text>
                          </AnimatedPressable>
                        </View>
                        <View style={{ flex: 1 }}>
                          <AnimatedPressable
                            onPress={() => handleTryDemo('investor')}
                            disabled={isLoading}
                            accessibilityRole="button"
                            accessibilityLabel="Demo as investor"
                            className="rounded-xl py-4 items-center gap-2"
                            style={{
                              backgroundColor: colors.primary[50],
                              borderWidth: 1.5,
                              borderColor: colors.primary[300],
                            }}
                          >
                            <TrendingUp size={24} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[700] }} className="font-semibold text-sm">
                              Investor
                            </Text>
                          </AnimatedPressable>
                        </View>
                        <View style={{ flex: 1 }}>
                          <AnimatedPressable
                            onPress={() => handleTryDemo('admin')}
                            disabled={isLoading}
                            accessibilityRole="button"
                            accessibilityLabel="Demo as admin"
                            className="rounded-xl py-4 items-center gap-2"
                            style={{
                              backgroundColor: colors.primary[50],
                              borderWidth: 1.5,
                              borderColor: colors.primary[300],
                            }}
                          >
                            <Shield size={24} color={colors.primary[600]} />
                            <Text style={{ color: colors.primary[700] }} className="font-semibold text-sm">
                              Admin
                            </Text>
                          </AnimatedPressable>
                        </View>
                      </View>
                      <AnimatedPressable
                        onPress={() => setShowDemoRolePicker(false)}
                        className="mt-3 items-center py-2"
                      >
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">
                          Cancel
                        </Text>
                      </AnimatedPressable>
                    </View>
                  )}
                </Animated.View>
              )}

              {/* Terms (signup only) */}
              {mode === 'signup' && (
                <Animated.View
                  entering={FadeInUp.delay(650).duration(500)}
                  className="px-8 pb-8"
                >
                  <Text style={{ color: colors.text.tertiary }} className="text-xs text-center leading-relaxed">
                    By creating an account, you agree to our{' '}
                    <Text
                      accessibilityRole="link"
                      accessibilityHint="Opens the Terms of Service"
                      onPress={() => router.push('/terms')}
                      style={{ color: colors.text.secondary, textDecorationLine: 'underline' }}
                      className="font-semibold"
                    >
                      Terms of Service
                    </Text>{' '}
                    and{' '}
                    <Text
                      accessibilityRole="link"
                      accessibilityHint="Opens the Privacy Policy"
                      onPress={() => router.push('/privacy')}
                      style={{ color: colors.text.secondary, textDecorationLine: 'underline' }}
                      className="font-semibold"
                    >
                      Privacy Policy
                    </Text>
                    .
                  </Text>
                </Animated.View>
              )}
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AuthLayout>
  );
}
